import type { Action } from "@/actions/action-types";
import { EditorIcon } from "@/components/environments/EditorIcon";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { useOpenEditor } from "@/hooks/use-open-editor";
import { useEnvironment, useStartEnvironment, useStopEnvironment } from "@/queries/environment-queries";
import { useRunner } from "@/queries/runner-queries";
import { canOpen, environmentHasReachedStablePhase, showStart, showStop } from "@/routes/environments/phase";
import { formatError, MaximumEnvironmentLifetimeExceededError } from "@/utils/errors";
import { IconChecklist } from "central-icons/IconChecklist";
import { IconCircleInfo } from "central-icons/IconCircleInfo";
import { IconClipboard } from "central-icons/IconClipboard";
import { IconConsole } from "central-icons/IconConsole";
import { IconPencil } from "central-icons/IconPencil";
import { IconPlay } from "central-icons/IconPlay";
import { IconStop } from "@/assets/icons/geist/IconStop";
import { IconTrashCan } from "central-icons/IconTrashCan";
import { IconTrashPermanently } from "central-icons/IconTrashPermanently";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { NAV_STATE_DETAILS, NAV_STATE_VSCODE } from "@/routes/environments/details/environment-nav-state";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

type UseEnvironmentActionsOptions = {
    /**
     * Called when the user selects "Rename". The consumer is responsible
     * for entering rename mode (e.g. switching to an inline input).
     */
    onRename?: () => void;
    /**
     * Called when the user selects "Delete" or "Force Delete".
     * The consumer is responsible for showing a confirmation modal.
     * Receives `{ force: boolean }`.
     */
    onDelete?: (opts: { force: boolean }) => void;
    /**
     * Called when the user selects "View Details".
     * Only shown when provided.
     */
    onViewDetails?: () => void;
    /**
     * Called when the environment lifetime exceeded error occurs during start.
     */
    onLifetimeExceeded?: () => void;
};

/**
 * Returns a flat list of actions for an environment. Each action carries
 * its own handler, disabled/hidden state, and metadata. Consumers render
 * these in their own component vocabulary (DropdownMenuItem, ContextMenuItem,
 * CommandItem).
 *
 * Actions that require consumer-specific UI (rename input, delete modal)
 * delegate via callbacks in `options`.
 */
export function useEnvironmentActions(
    environmentId: string | undefined,
    options: UseEnvironmentActionsOptions = {},
): Action[] {
    const { onRename, onDelete, onViewDetails, onLifetimeExceeded } = options;

    const { data: environment } = useEnvironment(environmentId);
    const { data: runner } = useRunner(environment?.metadata?.runnerId);
    const { toast } = useToast();
    const navigate = useNavigate();
    const openEditor = useOpenEditor();
    const startEnvironment = useStartEnvironment();
    const stopEnvironment = useStopEnvironment();

    const { selectedEditor, selectedEditorVersion, availableEditors } = useGetEditorForEnv(
        environmentId,
        environment?.metadata?.projectId,
    );

    const handleStart = useCallback(async () => {
        if (!environmentId) return;
        try {
            await startEnvironment.mutateAsync({ environmentId });
        } catch (error) {
            if (error instanceof MaximumEnvironmentLifetimeExceededError) {
                onLifetimeExceeded?.();
                return;
            }
            toast({ title: "Failed to start environment", description: formatError(error) });
        }
    }, [startEnvironment, environmentId, toast, onLifetimeExceeded]);

    const handleStop = useCallback(async () => {
        if (!environmentId) return;
        try {
            await stopEnvironment.mutateAsync(environmentId);
        } catch (error) {
            toast({ title: "Failed to stop environment", description: formatError(error) });
        }
    }, [stopEnvironment, environmentId, toast]);

    const handleCopyId = useCallback(async () => {
        if (!environmentId) return;
        await navigator.clipboard.writeText(environmentId);
        toast({ title: "Environment ID copied to clipboard", description: environmentId });
    }, [environmentId, toast]);

    const handleViewLogs = useCallback(() => {
        if (!environmentId) return;
        navigate(`/details/${environmentId}/logs`, NAV_STATE_DETAILS);
    }, [environmentId, navigate]);

    const handleViewTaskRuns = useCallback(() => {
        if (!environmentId) return;
        navigate(`/details/${environmentId}/tasks`, NAV_STATE_DETAILS);
    }, [environmentId, navigate]);

    return useMemo((): Action[] => {
        if (!environment) return [];

        const envCanOpen = canOpen(environment);
        const isStopped = showStart(environment);
        const canBeStopped = showStop(environment);
        const isStable = environmentHasReachedStablePhase(environment);
        const isDeleting = environment.spec?.desiredPhase === EnvironmentPhase.DELETED;
        const allowForceDelete = runner && runner.provider !== RunnerProvider.MANAGED;

        const actions: Action[] = [];

        // Start / Stop — shown first so the primary lifecycle action is always at the top
        actions.push({
            id: "start",
            label: "Start",
            icon: <IconPlay />,
            onSelect: handleStart,
            disabled: !isStopped || !isStable,
            hidden: !isStopped,
            group: "lifecycle",
            priority: 0,
        });

        actions.push({
            id: "stop",
            label: "Stop",
            icon: <IconStop size="sm" />,
            onSelect: handleStop,
            disabled: !canBeStopped,
            hidden: !canBeStopped,
            group: "lifecycle",
            priority: 1,
        });

        // "Open in VS Code Web" — switches to the embedded VS Code tab
        const vscodeBrowser = availableEditors?.find((e) => e.alias === "vscode-browser");
        if (vscodeBrowser && envCanOpen) {
            actions.push({
                id: "open-vscode-web",
                label: "Open in VS Code Web",
                icon: <EditorIcon editor={vscodeBrowser} size="sm" className="shrink-0" />,
                onSelect: () => navigate(`/details/${environmentId}`, NAV_STATE_VSCODE),
                disabled: !envCanOpen,
                hidden: !envCanOpen,
                group: "open",
                priority: -1,
            });
        }

        // Open in editor — only shown when the environment can be opened
        if (selectedEditor) {
            actions.push({
                id: "open-editor",
                label: `Open in ${selectedEditor.name}`,
                icon: <EditorIcon editor={selectedEditor} size="sm" className="shrink-0" />,
                onSelect: () =>
                    void openEditor(
                        selectedEditor,
                        environment,
                        selectedEditorVersion || selectedEditor.versions?.[0]?.version,
                    ),
                disabled: !envCanOpen,
                hidden: !envCanOpen,
                group: "open",
                priority: 0,
            });
        }

        if (availableEditors) {
            let editorPriority = 1;
            for (const editor of availableEditors) {
                if (editor.id === selectedEditor?.id) continue;
                // Skip vscode-browser since it's already listed as "Open in VS Code Web"
                if (editor.alias === "vscode-browser") continue;
                actions.push({
                    id: `open-editor-${editor.id}`,
                    label: `Open in ${editor.name}`,
                    icon: <EditorIcon editor={editor} size="sm" className="shrink-0" />,
                    onSelect: () => void openEditor(editor, environment, editor.versions?.[0]?.version),
                    disabled: !envCanOpen,
                    hidden: !envCanOpen,
                    group: "open",
                    priority: editorPriority++,
                });
            }
        }

        // View Logs
        actions.push({
            id: "view-logs",
            label: "View Logs",
            icon: <IconConsole />,
            onSelect: handleViewLogs,
            group: "navigate",
            priority: 0,
        });

        // View Task Runs
        actions.push({
            id: "view-task-runs",
            label: "View Task Runs",
            icon: <IconChecklist />,
            onSelect: handleViewTaskRuns,
            group: "navigate",
            priority: 1,
        });

        // View Details
        if (onViewDetails) {
            actions.push({
                id: "view-details",
                label: "View Details",
                icon: <IconCircleInfo />,
                onSelect: onViewDetails,
                group: "navigate",
                priority: 2,
            });
        }

        // Copy ID
        actions.push({
            id: "copy-id",
            label: "Copy ID",
            icon: <IconClipboard />,
            onSelect: handleCopyId,
            group: "info",
            priority: 0,
        });

        // Rename
        if (onRename) {
            actions.push({
                id: "rename",
                label: "Rename",
                icon: <IconPencil />,
                onSelect: onRename,
                group: "edit",
                priority: 0,
            });
        }

        // Delete
        if (onDelete && !isDeleting) {
            actions.push({
                id: "delete",
                label: "Delete",
                icon: <IconTrashCan />,
                onSelect: () => onDelete({ force: false }),
                variant: "destructive",
                group: "danger",
                priority: 0,
            });
        }

        // Force Delete
        if (onDelete && isDeleting && allowForceDelete) {
            actions.push({
                id: "force-delete",
                label: "Force Delete",
                icon: <IconTrashPermanently />,
                onSelect: () => onDelete({ force: true }),
                variant: "destructive",
                group: "danger",
                priority: 1,
            });
        }

        return actions;
    }, [
        environment,
        environmentId,
        runner,
        navigate,
        selectedEditor,
        selectedEditorVersion,
        availableEditors,
        openEditor,
        handleStart,
        handleStop,
        handleCopyId,
        handleViewLogs,
        handleViewTaskRuns,
        onRename,
        onDelete,
        onViewDetails,
    ]);
}
